import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
} from "../utils/auth";
import { clearAuth } from "../features/auth/authSlice";
import { analytics, ERROR_DISPLAYED } from "../analytics";

// Add a variable to hold the store reference
let store;

// Create an exported function to allow store injection
export const injectStore = (_store) => {
  store = _store;
};

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3005";
export const API_VERSION = "/v1";

const api = axios.create({
  baseURL: `${API_BASE_URL}${API_VERSION}`,
  headers: {
    "Content-Type": "application/json",
  },
});

// ==================== INTERCEPTOR LOGIC ====================

let isRefreshing = false;
let failedQueue = [];

/**
 * Processes the queue of failed requests.
 * @param {Object} error - The error object if the refresh failed.
 * @param {string|null} token - The new access token if the refresh succeeded.
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Request interceptor:
 * - Adds Authorization header with access token if available.
 */
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/** Response interceptor:
 * - Handles 401 errors by attempting to refresh the access token.
 * - Queues failed requests while a token refresh is in progress.
 * - Clears tokens and Redux state if refresh fails.
 */
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(
          `${api.defaults.baseURL}/auth/refresh-tokens`,
          { refreshToken },
        );

        const newAccessToken = data.access.token;
        const newRefreshToken = data.refresh.token;

        setTokens(newAccessToken, newRefreshToken);
        api.defaults.headers.common["Authorization"] =
          "Bearer " + newAccessToken;
        processQueue(null, newAccessToken);
        originalRequest.headers["Authorization"] = "Bearer " + newAccessToken;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        // Dispatch clearAuth to reset the Redux state
        if (store) {
          store.dispatch(clearAuth());
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Track API errors (skip 401s handled by refresh logic)
    if (error.response && error.response.status !== 401) {
      analytics.track(ERROR_DISPLAYED, {
        error_type: "api_error",
        endpoint: originalRequest?.url,
        method: originalRequest?.method,
        status_code: error.response.status,
      });
    }

    return Promise.reject(error);
  },
);

export default api;
